import { useRef, useState } from "react";
import { newsList } from "@/mocks/news";

export default function NewsStrip() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const scrollBy = (direction: 1 | -1) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * 340, behavior: "smooth" });
  };

  return (
    <section className="w-full border-y border-background-200 bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-[1440px] px-4 md:px-8">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs tracking-[0.18em] text-accent-600">
              <span className="inline-block h-3 w-0.5 bg-accent-500"></span>
              最新资讯
            </p>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">光影现场速递</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="上一条资讯"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors cursor-pointer hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="下一条资讯"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors cursor-pointer hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {newsList.length === 0 ? (
          <p className="rounded-lg border border-background-200 bg-background-50 px-6 py-10 text-center text-sm text-foreground-500">
            暂无最新资讯，敬请期待
          </p>
        ) : (
          <div ref={scrollerRef} className="no-scrollbar flex gap-4 overflow-x-auto pb-2 md:gap-5">
            {newsList.map((news) => (
              <article
                key={news.id}
                className="group w-[260px] shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:w-[320px]"
              >
                <button
                  type="button"
                  onClick={() => setActiveId(news.id)}
                  className="block w-full text-left cursor-pointer"
                  aria-label={`查看资讯：${news.title}`}
                >
                  <div className="relative h-[150px] w-full overflow-hidden bg-primary-950">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-0.5 text-[11px] text-background-50">
                      {news.category}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="line-clamp-2 text-sm font-medium leading-relaxed text-foreground-950 group-hover:text-primary-700">
                      {news.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                      <span>{news.date}</span>
                      <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                      <span>阅读 {news.readTime}</span>
                    </div>
                  </div>
                </button>
              </article>
            ))}
          </div>
        )}
      </div>

      {activeId && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-primary-950/60 p-4 animate-fade-in md:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveId(null)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-lg bg-background-50"
            onClick={(event) => event.stopPropagation()}
          >
            {(() => {
              const news = newsList.find((item) => item.id === activeId);
              if (!news) return null;
              return (
                <>
                  <div className="relative h-[180px] w-full overflow-hidden bg-primary-950">
                    <img src={news.image} alt={news.title} className="h-full w-full object-cover object-top" />
                    <button
                      type="button"
                      onClick={() => setActiveId(null)}
                      aria-label="关闭资讯弹窗"
                      className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md bg-primary-950/70 text-background-50 cursor-pointer"
                    >
                      <i className="ri-close-line" aria-hidden="true"></i>
                    </button>
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">{news.category}</span>
                      <span>{news.date}</span>
                      <span>阅读 {news.readTime}</span>
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                      {news.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">{news.summary}</p>
                    <p className="mt-4 rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-500">
                      完整资讯页正在建设中，本页为演示预览。
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveId(null)}
                      className="mt-5 w-full rounded-md bg-primary-800 px-4 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-700"
                    >
                      我知道了
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
}